import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Download, ChatText, CheckCircle, WarningCircle, XCircle, Lightning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

export default function Portal() {
  const { pid, token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [activeDoc, setActiveDoc] = useState(null);
  const [ident, setIdent] = useState({ name: "", email: "", designation: "" });
  const [comment, setComment] = useState("");
  const [actionName, setActionName] = useState("");
  const [actionComments, setActionComments] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    pub.get(`/portal/${pid}/${token}`)
      .then(({ data }) => {
        setState({ loading: false, error: null, data });
        if (data.docs?.length) setActiveDoc((d) => d || data.docs[0].id);
        if (data.client_email) setIdent((i) => ({ ...i, email: i.email || data.client_email }));
        if (data.already_responded) setDone(data.already_responded.action);
      })
      .catch((e) => {
        const status = e?.response?.status;
        setState({ loading: false, error: e?.response?.data?.detail || "This link is invalid.",
                   errorKind: status === 410 ? "expired" : "invalid", data: null });
      });
  };
  useEffect(load, [pid, token]);

  if (state.loading) {
    return <div className="min-h-screen bg-[var(--c-bg)] flex items-center justify-center font-mono text-xs tracking-widest text-[var(--c-muted)]" data-testid="portal-loading">LOADING REVIEW PORTAL…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[var(--c-bg)] flex items-center justify-center p-6" data-testid="portal-error">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-8 max-w-md text-center">
          <XCircle size={28} className="mx-auto text-[var(--c-red)]" />
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-amber)] mt-3" data-testid="portal-error-kind">
            {state.errorKind === "expired" ? "Link expired or revoked" : "Invalid link"}
          </div>
          <div className="text-sm text-[var(--c-text)] mt-2">{state.error}</div>
          <div className="text-xs text-[var(--c-muted)] mt-2">Please contact your project manager for a new link.</div>
        </div>
      </div>
    );
  }

  const d = state.data;
  const doc = d.docs.find((x) => x.id === activeDoc);

  const identify = async () => {
    setBusy(true);
    try {
      await pub.post(`/portal/${pid}/${token}/identify`, ident);
      setState((s) => ({ ...s, data: { ...s.data, identified: true } }));
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const postComment = async () => {
    if (comment.trim().length < 2) return;
    setBusy(true);
    try {
      await pub.post(`/portal/${pid}/${token}/comment`, { doc_id: activeDoc, text: comment.trim() });
      setComment("");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      await pub.post(`/portal/${pid}/${token}/action`, { action, name: actionName.trim(), comments: actionComments.trim() });
      setDone(action);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!d.identified) {
    return (
      <div className="min-h-screen bg-[var(--c-bg)] flex items-center justify-center p-6" data-testid="portal-identify-gate">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-1">
            <Lightning size={18} weight="fill" className="text-[var(--c-accent)]" />
            <span className="text-sm font-medium text-[var(--c-text)]">Formigen · Document Review Portal</span>
          </div>
          <div className="text-xs text-[var(--c-muted)] mb-5">{d.project.code} · {d.project.summary} · Rev {d.rev}</div>
          <div className="text-xs text-[var(--c-text)] mb-3">Please confirm your details to begin the review.</div>
          <div className="space-y-3">
            <input data-testid="portal-name-input" value={ident.name} onChange={(e) => setIdent({ ...ident, name: e.target.value })}
              placeholder="Full name" className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)]" />
            <input data-testid="portal-email-input" type="email" value={ident.email} onChange={(e) => setIdent({ ...ident, email: e.target.value })}
              placeholder="Email" className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)]" />
            <input data-testid="portal-designation-input" value={ident.designation} onChange={(e) => setIdent({ ...ident, designation: e.target.value })}
              placeholder="Designation / role (optional)" className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)]" />
            <button data-testid="portal-identify-btn" onClick={identify} disabled={busy || ident.name.trim().length < 2 || !ident.email.includes("@")}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white text-xs rounded-sm py-2 disabled:opacity-40 transition-colors duration-150">
              Continue to review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex flex-col" data-testid="portal-page">
      <div className="h-14 shrink-0 bg-[var(--c-surface)] border-b border-[var(--c-border)] flex items-center gap-3 px-5">
        <Lightning size={17} weight="fill" className="text-[var(--c-accent)]" />
        <span className="text-sm font-medium text-[var(--c-text)]">Document Review Portal</span>
        <span className="font-mono text-[10px] text-[var(--c-muted)]">{d.project.code} · {d.project.summary}</span>
        <span className="ml-auto font-mono text-[10px] px-2 py-1 border border-[var(--c-amber)]/50 text-[var(--c-amber)] rounded-sm" data-testid="portal-rev-badge">
          REV {d.rev} — UPDATED {String(d.generated_at).slice(0, 10)}
        </span>
      </div>

      <div className="flex-1 flex min-h-0">
        <div className="w-64 shrink-0 bg-[var(--c-surface)] border-r border-[var(--c-border)] p-3 space-y-1" data-testid="portal-doc-list">
          {d.docs.map((x) => (
            <button key={x.id} data-testid={`portal-doc-${x.id}`} onClick={() => setActiveDoc(x.id)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-sm text-left text-xs transition-colors duration-100 ${
                activeDoc === x.id ? "bg-[var(--c-accent)]/15 text-[var(--c-text)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              <FileText size={13} className="shrink-0" /> <span className="truncate">{x.title}</span>
            </button>
          ))}
          <a href={`${API}/portal/${pid}/${token}/download-all`}
            className="flex items-center gap-2 px-3 py-2.5 text-xs text-[var(--c-accent)] hover:bg-[var(--c-surface-2)] rounded-sm" data-testid="portal-download-all">
            <Download size={13} /> Download all (ZIP)
          </a>
        </div>

        <div className="flex-1 min-w-0 overflow-y-auto p-5" data-testid="portal-viewer">
          {doc && (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-medium text-[var(--c-text)]">{doc.title}</h3>
                <a href={`${API}/portal/${pid}/${token}/download/${doc.id}`} data-testid={`portal-download-${doc.id}`}
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono text-[var(--c-muted)] hover:text-[var(--c-text)]">
                  <Download size={11} /> PDF
                </a>
              </div>
              {doc.type === "ga" ? (
                <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3" data-testid="portal-ga-view"
                  dangerouslySetInnerHTML={{ __html: doc.svg }} />
              ) : (
                <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto" data-testid={`portal-table-${doc.id}`}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[var(--c-border)]">
                        {doc.head.map((h) => <th key={h} className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {doc.rows.map((r, i) => (
                        <tr key={i} className="border-b border-[var(--c-border)]/50 last:border-0">
                          {r.map((c, j) => <td key={j} className={`px-3 py-1.5 ${j === 0 ? "text-[var(--c-text)]" : "font-mono text-[var(--c-muted)]"}`}>{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="w-80 shrink-0 bg-[var(--c-surface)] border-l border-[var(--c-border)] flex flex-col" data-testid="portal-side">
          <div className="p-3 border-b border-[var(--c-border)] text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Comments</div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="portal-comments">
            {d.comments.filter((c) => c.doc_id === activeDoc).map((c) => (
              <div key={c.id} className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-2.5" data-testid={`portal-comment-${c.id.slice(0, 8)}`}>
                <div className="text-[10px] text-[var(--c-muted)]">{c.by_name} · Rev {c.rev}{c.previous_rev ? " (previous revision)" : ""}</div>
                <div className="text-xs text-[var(--c-text)] mt-0.5">{c.text}</div>
              </div>
            ))}
            {d.comments.filter((c) => c.doc_id === activeDoc).length === 0 && (
              <div className="text-[10px] text-[var(--c-muted)] font-mono">No comments on this document yet.</div>
            )}
          </div>
          <div className="p-3 border-t border-[var(--c-border)] space-y-2">
            <div className="flex gap-2">
              <input data-testid="portal-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder={`Comment on ${doc?.title || "document"}…`}
                className="flex-1 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2.5 py-1.5 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)]" />
              <button data-testid="portal-comment-btn" onClick={postComment} disabled={busy || comment.trim().length < 2}
                className="px-2.5 bg-[var(--c-border)] text-[var(--c-text)] rounded-sm hover:bg-[var(--c-border)]/70 disabled:opacity-40">
                <ChatText size={13} />
              </button>
            </div>
          </div>
          <div className="p-3 border-t border-[var(--c-border)] space-y-2" data-testid="portal-actions">
            {done ? (
              <div className="text-center py-2" data-testid="portal-done">
                <CheckCircle size={22} weight="fill" className="mx-auto text-[var(--c-green)]" />
                <div className="text-xs text-[var(--c-text)] mt-2">
                  Thank you. Your {done === "approve" ? "approval" : done === "approve_comments" ? "approval (with comments)" : "rejection"} has been recorded.
                </div>
                {d.pm && <div className="text-[10px] text-[var(--c-muted)] mt-1">{d.pm.name} ({d.pm.email}) will follow up.</div>}
              </div>
            ) : (
              <>
                <input data-testid="portal-action-name" value={actionName} onChange={(e) => setActionName(e.target.value)}
                  placeholder="Type your name to acknowledge"
                  className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2.5 py-1.5 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)]" />
                <textarea data-testid="portal-action-comments" value={actionComments} onChange={(e) => setActionComments(e.target.value)}
                  placeholder="Comments / rejection reasons…" rows={2}
                  className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2.5 py-1.5 text-xs text-[var(--c-text)] outline-none focus:border-[var(--c-accent)] resize-none" />
                <button data-testid="portal-approve-btn" onClick={() => act("approve")} disabled={busy || actionName.trim().length < 2}
                  className="w-full flex items-center justify-center gap-1.5 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white text-xs rounded-sm py-2 disabled:opacity-40">
                  <CheckCircle size={13} weight="bold" /> Approve
                </button>
                <button data-testid="portal-approve-comments-btn" onClick={() => act("approve_comments")} disabled={busy || actionName.trim().length < 2 || actionComments.trim().length < 5}
                  className="w-full flex items-center justify-center gap-1.5 bg-[var(--c-amber)] hover:bg-[var(--c-amber)]/85 text-[var(--c-text)] text-xs rounded-sm py-2 disabled:opacity-40">
                  <WarningCircle size={13} weight="bold" /> Approve with Comments
                </button>
                <button data-testid="portal-reject-btn" onClick={() => act("reject")} disabled={busy || actionName.trim().length < 2 || actionComments.trim().length < 5}
                  className="w-full flex items-center justify-center gap-1.5 bg-[var(--c-red)] hover:bg-[var(--c-red)]/85 text-[var(--c-text)] text-xs rounded-sm py-2 disabled:opacity-40">
                  <XCircle size={13} weight="bold" /> Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
